import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AppTablePagination from "../../component/common/appTablePagination";
import RefreshIcon from "@mui/icons-material/Refresh";
import React, { useCallback, useEffect, useState } from "react";
import invoiceService from "../../../services/InvoiceService";
import saleOrderServices from "../../../services/SalesOrderService";
import { InvoiceType } from "../../../types/invoice&payment";
import ViewReceiptModal from "./ViewReceiptModal";
import { useTranslation } from "react-i18next";
import { onPaymentsEvent } from "../../../services/paymentsEvents";
import dashboardServices from "../../../services/DashboardServices";
import {
  formatWalletDisplay,
  normalizeBalanceSummaryResponse,
} from "../../../helper/formatAppBalance";
import {
  formatIqdAmount,
  formatPaymentDisplayDate,
  formatPaymentMethodLabel,
  enrichInvoiceWithPayment,
  formatDisplayInvoiceNumber,
  isCodOrderNumber,
  formatSagePaymentRef,
  pickPaymentDate,
  pickSagePaymentNumber,
  resolveInvoiceAmountPaid,
} from "../../../helper/paymentDisplayUtils";
import { localizePaymentStatus } from "../../../helper/localizePaymentStatus";
import { NJIM } from "../../../theme/njimTheme";

// Strip "—"/"-"/whitespace-only placeholders the backend sends before invoice sync
const cleanInvField = (v: unknown): string => {
  const s = String(v ?? "").trim();
  return /^[-—\s]*$/.test(s) ? "" : s;
};

// Normalize raw API response to InvoiceType — mirrors the mobile app's normalizeInvoice
const normalizeInvoice = (raw: Record<string, unknown>): InvoiceType => {
  const orderNumber = String(raw?.orderNumber ?? raw?.orderId ?? "");
  const rawInvoiceNumber =
    cleanInvField(raw?.invoiceNumber) ||
    cleanInvField(raw?.number) ||
    cleanInvField(raw?.orderNumber) ||
    "";

  return {
    id: String(raw?.id ?? ""),
    receiptInvoiceId: String(raw?.id ?? ""),
    invoiceNumber: formatDisplayInvoiceNumber(rawInvoiceNumber, orderNumber),
    orderNumber,
  // Ignore C# default DateTime (0001-01-01) — treat as no due date
  dueDate: typeof raw?.dueDate === "string" && !String(raw.dueDate).startsWith("0001") ? raw.dueDate : undefined,
  paymentDate: pickPaymentDate(raw),
  subtotal: Number(raw?.subtotal ?? raw?.subTotal ?? 0),
  total: Number(raw?.total ?? 0),
  amountDue: Number(raw?.amountDue ?? 0),
  remainingBalance: Number(raw?.remainingBalance ?? 0),
  paymentStatus:
    typeof raw?.paymentStatus === "string"
      ? raw.paymentStatus
      : typeof raw?.status === "string"
      ? raw.status
      : undefined,
  status: typeof raw?.status === "string" ? raw.status : undefined,
  createdDate:
    typeof raw?.createdAt === "string" ? raw.createdAt
    : typeof raw?.createdDate === "string" ? raw.createdDate
    : typeof raw?.paymentDate === "string" ? raw.paymentDate
    : typeof raw?.receiptGeneratedDate === "string" ? raw.receiptGeneratedDate
    : typeof raw?.dueDate === "string" ? raw.dueDate
    : undefined,
  };
};

// Derive a sort date from "SO-{month}-{year}-{seq}" order number as a last resort
const dateFromOrderNumber = (num: string): string | undefined => {
  const m = num.match(/^SO-(\d{1,2})-(\d{4})-/i);
  if (!m) return undefined;
  return new Date(Number(m[2]), Number(m[1]) - 1, 1).toISOString();
};

// Convert a SalesOrder into an InvoiceType row so web orders show on the invoices page
// even before the Sage X3 sync creates a real invoice document for them.
const normalizeOrderAsInvoice = (raw: Record<string, unknown>): InvoiceType => {
  const total = Number(raw?.total ?? raw?.amount ?? 0);
  const isPaid = String(raw?.paymentStatus ?? "").toUpperCase() === "PAID";
  const orderNum = String(raw?.number ?? "");
  const mongoId = String(raw?.id ?? "");
  const createdDate =
    typeof raw?.createdAt === "string" ? raw.createdAt
    : typeof raw?.createdDate === "string" ? raw.createdDate
    : typeof raw?.date === "string" ? raw.date
    : typeof raw?.orderDate === "string" ? raw.orderDate
    : typeof raw?.updatedAt === "string" ? raw.updatedAt
    : dateFromOrderNumber(orderNum);
  return {
    id: mongoId,
    invoiceNumber: formatDisplayInvoiceNumber(undefined, orderNum),
    orderNumber: orderNum,
    total,
    subtotal: Number(raw?.subTotal ?? raw?.amount ?? 0),
    amountDue: total,
    remainingBalance: isPaid ? 0 : total,
    amount: isPaid ? 0 : total,
    paymentStatus: isPaid ? "Paid" : "Pending",
    status: "Pending",
    dueDate: undefined,
    createdDate,
  };
};

const InvoicesPayments = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [allInvoices, setAllInvoices] = useState<InvoiceType[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceType | null>(null);
  const [viewReceiptModal, setViewReceiptModal] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [activeSection, setActiveSection] = useState<"invoices" | "wallet">("invoices");
  const [appWalletBalance, setAppWalletBalance] = useState(0);
  const [balanceCurrency, setBalanceCurrency] = useState("IQD");

  const fetchWalletBalance = useCallback(async () => {
    try {
      const response = await dashboardServices.getBalanceSummary();
      const balances = normalizeBalanceSummaryResponse(response?.data);
      setAppWalletBalance(balances.appWalletBalance);
      setBalanceCurrency(balances.currency);
    } catch (error) {
      console.error("[Invoices] wallet balance error:", error);
    }
  }, []);

  const fetchInvoiceList = useCallback(async () => {
    try {
      setLoading(true);
      setApiError(null);

      // Invoices: resilient fetch (search → fallback GET /invoices, silent — no error toast).
      const [rawInvoices, ordersRes, paymentsRes] = await Promise.all([
        invoiceService.fetchInvoicesForPortal(),
        saleOrderServices.getAllOrders(5000).catch(() => ({ data: [] as unknown[] })),
        invoiceService.getAllPayments().catch(() => ({ data: [] as unknown[] })),
      ]);
      const normalizedInvoices = rawInvoices.map((item) =>
        normalizeInvoice(item as Record<string, unknown>)
      );

      // Build a set of order numbers already covered by a real invoice
      const coveredOrderNumbers = new Set(
        normalizedInvoices.map((inv) => (inv.orderNumber ?? "").toLowerCase().trim())
      );

      // Build lookup: invoice MongoDB ID → invoice number (e.g. "INV-1779277381156")
      // Add paid web orders that have no corresponding invoice yet
      // getSaleOrderList returns { items: [], totalCount } — handle both shapes
      const rawOrders: unknown[] = Array.isArray(ordersRes?.data?.items)
        ? ordersRes.data.items
        : Array.isArray(ordersRes?.data)
        ? ordersRes.data
        : [];

      const pendingSageLabel = t("invoicesPayments.pendingSageSync", {
        defaultValue: "Pending Sage sync",
      });

      // Build lookups: invoice/order MongoDB ID → successful payment record
      const rawPaymentsList = Array.isArray(paymentsRes?.data)
        ? (paymentsRes.data as Record<string, unknown>[])
        : [];
      const paymentByOrderId = new Map<string, Record<string, unknown>>();
      const paymentByInvoiceId = new Map<string, Record<string, unknown>>();
      // COD invoice payments carry the Sage invoice numbers they paid (invoiceNumbers /
      // paymentReference), so a COD row is matched by its invoice, never by its order.
      const paymentByInvoiceNumber = new Map<string, Record<string, unknown>>();
      rawPaymentsList.forEach((p) => {
        if (String(p.status ?? p.Status ?? "").toUpperCase() !== "SUCCESS") return;
        const orderId = String(p.orderId ?? p.OrderId ?? "");
        if (orderId) paymentByOrderId.set(orderId, p);
        const invoiceId = String(p.invoiceId ?? p.InvoiceId ?? "");
        if (invoiceId && invoiceId.toLowerCase() !== "wallet") {
          paymentByInvoiceId.set(invoiceId, p);
        }
        const numbers = new Set<string>();
        const listed = p.invoiceNumbers ?? p.InvoiceNumbers;
        if (Array.isArray(listed)) listed.forEach((n) => numbers.add(String(n).trim().toLowerCase()));
        String(p.paymentReference ?? p.PaymentReference ?? "")
          .split(",")
          .forEach((n) => numbers.add(n.trim().toLowerCase()));
        numbers.forEach((n) => {
          if (n) paymentByInvoiceNumber.set(n, p);
        });
      });

      const orderIdByNumber = new Map<string, string>();
      rawOrders.forEach((item) => {
        const order = item as Record<string, unknown>;
        const num = String(order?.number ?? "").toLowerCase().trim();
        const id = String(order?.id ?? "");
        if (num && id) orderIdByNumber.set(num, id);
      });

      const resolvePaymentForRow = (row: InvoiceType): Record<string, unknown> | undefined => {
        if (row.id && paymentByInvoiceId.has(String(row.id))) {
          return paymentByInvoiceId.get(String(row.id));
        }
        const invoiceKey = (row.invoiceNumber ?? "").toLowerCase().trim();
        if (invoiceKey && paymentByInvoiceNumber.has(invoiceKey)) {
          return paymentByInvoiceNumber.get(invoiceKey);
        }
        // COD rows are paid per invoice: an order-level match would attach the wrong payment.
        if (isCodOrderNumber(row.orderNumber)) return undefined;
        const orderKey = (row.orderNumber ?? "").toLowerCase().trim();
        const orderId = orderIdByNumber.get(orderKey);
        if (orderId && paymentByOrderId.has(orderId)) {
          return paymentByOrderId.get(orderId);
        }
        return undefined;
      };

      const applyPaymentRef = (row: InvoiceType): InvoiceType =>
        enrichInvoiceWithPayment(row, resolvePaymentForRow(row));

      // Build a map of order number → paymentStatus so we can show Sage invoices
      // whose associated order was paid via wallet (wallet payments don't clear the
      // Sage RemBalance, so those invoices arrive with PaymentStatus = Pending).
      const orderPaidSet = new Set<string>();
      rawOrders.forEach((item) => {
        const order = item as Record<string, unknown>;
        const num = String(order?.number ?? "").toLowerCase().trim();
        const status = String(order?.paymentStatus ?? "").toUpperCase();
        if (num && status === "PAID") orderPaidSet.add(num);
      });

      const orderRows: InvoiceType[] = rawOrders
        .map((item) => item as Record<string, unknown>)
        .filter((order) => {
          const num = String(order?.number ?? "").toLowerCase().trim();
          return num && !coveredOrderNumbers.has(num);
        })
        .map((order) => {
          const row = normalizeOrderAsInvoice(order);
          const payment = paymentByOrderId.get(String(order?.id ?? ""));
          if (payment?.invoiceId) {
            const invoiceId = String(payment.invoiceId);
            if (invoiceId.toLowerCase() !== "wallet") {
              row.receiptInvoiceId = invoiceId;
            }
          }
          return applyPaymentRef(row);
        });

      const enrichedInvoices = normalizedInvoices.map(applyPaymentRef);

      // Wallet top-up rows — successful recharge transactions
      const walletRows: InvoiceType[] = rawPaymentsList
        .filter((p) =>
          String(p.invoiceId ?? p.InvoiceId ?? "").toLowerCase() === "wallet" &&
          String(p.status ?? p.Status ?? "").toUpperCase() === "SUCCESS"
        )
        .map((p): InvoiceType => {
          const amount = Number(p.amount ?? p.Amount ?? 0);
          const paymentDate = pickPaymentDate(p);
          const sageRaw = pickSagePaymentNumber(p);
          const paymentType = String(p.type ?? p.Type ?? "");
          return {
            id: String(p.id ?? p.Id ?? ""),
            invoiceNumber: formatSagePaymentRef(sageRaw, pendingSageLabel),
            sagePaymentNumber: sageRaw,
            orderNumber: "Wallet Top-Up",
            total: amount,
            subtotal: amount,
            amountDue: 0,
            remainingBalance: 0,
            amountPaid: amount,
            paymentStatus: "Paid",
            status: "Paid",
            paymentMethod: formatPaymentMethodLabel(paymentType, "Wallet Top-Up"),
            transactionID: String(p.transactionId ?? p.TransactionId ?? ""),
            paymentDate,
            createdDate: paymentDate,
            dueDate: paymentDate,
          };
        });

      const merged = [...enrichedInvoices, ...orderRows, ...walletRows];

      // Filter: only show paid items — hide pending and failed payments.
      // Also include Sage invoices whose order was paid via wallet: wallet payments
      // only update MongoDB (not Sage), so Sage RemBalance stays > 0 and the synced
      // invoice arrives as PaymentStatus = Pending even though the order IS paid.
      const paidOnly = merged
        .filter((inv) => {
          const directPaid = (inv.paymentStatus ?? "").toLowerCase() === "paid";
          const orderPaid = orderPaidSet.has((inv.orderNumber ?? "").toLowerCase().trim());
          return directPaid || orderPaid;
        })
        .map((inv) => {
          const orderPaid = orderPaidSet.has((inv.orderNumber ?? "").toLowerCase().trim());
          if (!orderPaid || (inv.paymentStatus ?? "").toLowerCase() === "paid") {
            return inv;
          }
          // Wallet/app payment settled the order; Sage RemBalance is not customer-facing due.
          return { ...inv, paymentStatus: "Paid", remainingBalance: 0 };
        });

      // Sort newest first
      paidOnly.sort((a, b) => {
        const da = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const db = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return db - da;
      });
      setAllInvoices(paidOnly);
    } catch (error: unknown) {
      console.error("[Invoices] fetch error:", error);
      setApiError(
        t("invoicesPayments.loadFailed", {
          defaultValue: "Could not load invoice data. Please try again.",
        })
      );
      setAllInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchInvoiceList();
    fetchWalletBalance();
  }, [fetchInvoiceList, fetchWalletBalance]);

  useEffect(() => {
    return onPaymentsEvent(() => {
      fetchInvoiceList();
      fetchWalletBalance();
    });
  }, [fetchInvoiceList, fetchWalletBalance]);

  const invoiceItems = allInvoices.filter((inv) => inv.orderNumber !== "Wallet Top-Up");
  const walletItems = allInvoices.filter((inv) => inv.orderNumber === "Wallet Top-Up");
  const displayedItems = activeSection === "invoices" ? invoiceItems : walletItems;

  const invoicesTotal = invoiceItems.reduce(
    (sum, inv) => sum + (Number(inv.total) || 0),
    0
  );

  const walletTopUpTotal = walletItems.reduce(
    (sum, inv) => sum + (Number(inv.total) || 0),
    0
  );

  const summaryLabel =
    activeSection === "wallet"
      ? t("invoicesPayments.walletBalance")
      : t("common.totalAmount");

  const summaryValue =
    activeSection === "wallet"
      ? formatWalletDisplay(appWalletBalance, balanceCurrency)
      : formatIqdAmount(invoicesTotal);

  // Client-side pagination
  const paginatedInvoices = displayedItems.slice(page * pageSize, (page + 1) * pageSize);

  const getStatusStyles = (status: string | number | undefined) => {
    const statusString = String(status).toLowerCase();
    if (statusString === "pending") {
      return {
        background: "rgba(245,158,11,0.08)",
        color: NJIM.warning,
        border: "1px solid rgba(245,158,11,0.2)",
      };
    }
    if (statusString === "paid") {
      return {
        background: "rgba(22,163,74,0.08)",
        color: NJIM.success,
        border: "1px solid rgba(22,163,74,0.2)",
      };
    }
    if (statusString === "overdue" || statusString === "rejected") {
      return {
        background: "rgba(220,38,38,0.08)",
        color: NJIM.danger,
        border: "1px solid rgba(220,38,38,0.2)",
      };
    }
    return {
      background: "rgba(10,61,120,0.06)",
      color: NJIM.ink,
      border: "1px solid rgba(10,61,120,0.15)",
    };
  };

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary, letterSpacing: "-0.3px", fontSize: "1.5rem" }}>
            {t("invoicesPayments.title")}
          </Typography>
          <Tooltip title={t("invoicesPayments.refresh")}>
            <IconButton onClick={fetchInvoiceList} disabled={loading} size="small" sx={{ color: NJIM.ink }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography sx={{ color: NJIM.textSecondary, mb: 3, fontSize: "15px" }}>
          {t("invoicesPayments.manageInvoicesPayments")}
        </Typography>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06) !important", transform: "translateY(-2px)" } }}>
              <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, fontWeight: 500 }}>
                {summaryLabel}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "22px", mt: 1.5, color: NJIM.textPrimary }}>
                {summaryValue}
              </Typography>
              {activeSection === "wallet" && walletTopUpTotal > 0 && (
                <Typography sx={{ fontSize: 12, color: NJIM.textMuted, mt: 0.5 }}>
                  {t("invoicesPayments.topUpHistoryTotal")}{" "}
                  {formatIqdAmount(walletTopUpTotal)}
                </Typography>
              )}
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2.5, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.06) !important", transform: "translateY(-2px)" } }}>
              <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, fontWeight: 500 }}>
                {t("invoicesPayments.allInvoices")}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "22px", mt: 1.5, color: NJIM.textPrimary }}>
                {displayedItems.length}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Pill toggle */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box
            sx={{
              display: "inline-flex",
              background: "rgba(241,245,249,0.9)",
              borderRadius: "50px",
              p: "4px",
              border: "1px solid rgba(226,232,240,0.6)",
            }}
          >
            {(["invoices", "wallet"] as const).map((section) => (
              <Box
                key={section}
                onClick={() => {
                  setActiveSection(section);
                  setPage(0);
                }}
                sx={{
                  px: 3.5,
                  py: 1,
                  borderRadius: "50px",
                  cursor: "pointer",
                  fontWeight: activeSection === section ? 700 : 500,
                  fontSize: "14px",
                  color: activeSection === section ? NJIM.textPrimary : NJIM.textSecondary,
                  background: activeSection === section ? NJIM.white : "transparent",
                  boxShadow: activeSection === section ? "0 1px 6px rgba(0,0,0,0.08)" : "none",
                  transition: "all 0.2s ease",
                  userSelect: "none",
                }}
              >
                {section === "invoices"
                  ? t("invoicesPayments.invoicesTab")
                  : t("invoicesPayments.walletTab")}
              </Box>
            ))}
          </Box>
        </Box>

        <Card sx={{ p: 2.5, mt: 3 }}>
          <Typography sx={{ mb: 2.5, fontWeight: 600, color: NJIM.textPrimary, fontSize: "16px" }}>
            {activeSection === "invoices"
              ? t("invoicesPayments.allInvoices")
              : t("invoicesPayments.walletTopUps")}
          </Typography>
          <Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: 5 }}>
                <CircularProgress size={44} sx={{ color: NJIM.ink }} />
              </Box>
            ) : apiError ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography sx={{ color: NJIM.danger, fontWeight: 600, mb: 1 }}>
                  {apiError}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchInvoiceList}
                  sx={{ textTransform: "none", borderColor: NJIM.ink, color: NJIM.ink, mt: 1 }}
                >
                  {t("invoicesPayments.retry")}
                </Button>
              </Box>
            ) : displayedItems.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography sx={{ color: NJIM.textSecondary, fontSize: "15px" }}>
                  {t("invoicesPayments.noInvoicesFound")}
                </Typography>
              </Box>
            ) : (
              <>
                <Table
                  sx={{
                    border: "1px solid rgba(226,232,240,0.5)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": {
                      padding: "12px 14px",
                      borderBottom: "1px solid rgba(226,232,240,0.4)",
                      fontSize: "13px",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ background: "rgba(248,249,250,0.5)" }}>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("invoicesPayments.invoiceNumber")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("invoicesPayments.orderNumber")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("common.totalAmount")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("invoicesPayments.amountPaid")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("invoicesPayments.paymentDate", { defaultValue: "Payment Date" })}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: NJIM.textSecondary, fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        {t("common.status")}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedInvoices.map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        sx={{
                          transition: "background 0.2s ease",
                          "&:hover": { background: "rgba(248,249,250,0.5)" },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>
                          {invoice.invoiceNumber}
                        </TableCell>
                        <TableCell sx={{ color: NJIM.textSecondary }}>
                          {activeSection === "wallet" && invoice.paymentMethod
                            ? `${invoice.orderNumber} · ${invoice.paymentMethod}`
                            : invoice.orderNumber}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>
                          {formatIqdAmount(invoice.total)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, color: NJIM.textPrimary }}>
                          {formatIqdAmount(resolveInvoiceAmountPaid(invoice))}
                        </TableCell>
                        <TableCell sx={{ color: NJIM.textSecondary }}>
                          {formatPaymentDisplayDate(
                            invoice.paymentDate ?? invoice.createdDate ?? invoice.dueDate
                          )}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              py: 0.5,
                              px: 1.2,
                              borderRadius: "6px",
                              width: "fit-content",
                              fontSize: "12px",
                              fontWeight: 600,
                              ...getStatusStyles(invoice.paymentStatus),
                            }}
                          >
                            {localizePaymentStatus(invoice.paymentStatus, t)}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setViewReceiptModal(true);
                            }}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "12px",
                              borderRadius: "8px",
                              borderColor: "rgba(226,232,240,0.8)",
                              color: NJIM.textSecondary,
                              transition: "all 0.2s ease",
                              "&:hover": { borderColor: NJIM.ink, color: NJIM.ink, background: "rgba(10,61,120,0.04)" },
                            }}
                          >
                            {t("invoicesPayments.viewReceipt")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Grid
                  style={{
                    justifyContent: "flex-end",
                    alignItems: "center",
                    display: "flex",
                    marginTop: 16,
                    padding: 8,
                  }}
                >
                  <AppTablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={displayedItems.length}
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    sx={{
                      color: NJIM.textSecondary,
                      "& .MuiSelect-icon": { color: NJIM.textSecondary },
                      "& .MuiSvgIcon-root": { color: NJIM.textSecondary },
                    }}
                  />
                </Grid>
              </>
            )}
          </Box>
        </Card>
      </Box>
      <ViewReceiptModal
        open={viewReceiptModal}
        onClose={() => setViewReceiptModal(false)}
        invoiceData={selectedInvoice}
      />
    </>
  );
};

export default InvoicesPayments;
